import { capitalisedTermForCompany } from "../../copy/sharedCopy";
import {
    DriverDeclaration,
    DeclarationsOrderByOptions,
} from "../../models/api/admin/eDeclarations";
import { declarationStatusColor } from "../../utils/declararationStatusColour";
import { formatDateToString } from "../../utils/formatDateToString";
import StatusPill from "../ReusableComponents/StatusPill/StatusPill";
import {
    type TableContent,
    type TableHeader,
} from "../ReusableComponents/Table";
import UserIcon from "../UserIcon";

export const declarationTableAllowedIdsEn = {
    name: "name",
    company: "company",
    department: "department",
    email: "email",
    declarationExpires: "declarationExpires",
    declarationLastSent: "declarationLastSent",
    licenceOrigin: "licenceOrigin",
    type: "type",
    status: "status",
    inFlightStatus: "inFlightStatus",
    inFlightType: "inFlightType",
} as const;

export type DeclarationsTableAllowedIds =
    (typeof declarationTableAllowedIdsEn)[keyof typeof declarationTableAllowedIdsEn];

enum MenuGroups {
    PersonalDetails = "Personal Details",
    Employment = "Employment",
    Declaration = "Declaration",
}

export const driverDeclarationsMenuGroups: MenuGroups[] = (
    Object.keys(MenuGroups) as Array<keyof typeof MenuGroups>
).map((key) => MenuGroups[key]);

export const declarationsOrderByOptions = [
    DeclarationsOrderByOptions.LastName,
    DeclarationsOrderByOptions.Company,
    DeclarationsOrderByOptions.Department,
    DeclarationsOrderByOptions.EmailAddress,
    DeclarationsOrderByOptions.LicenceNumber,
    DeclarationsOrderByOptions.DateOfBirth,
    DeclarationsOrderByOptions.EmployeeId,
    DeclarationsOrderByOptions.ExternalReference,
    DeclarationsOrderByOptions.AddressPostcode,
    DeclarationsOrderByOptions.Company,
    DeclarationsOrderByOptions.Department,
];

export type DriverDeclarationsRowContent = TableContent<
    DriverDeclaration,
    DeclarationsTableAllowedIds
>;
export type DriverDeclarationsTableHeader = TableHeader<
    DeclarationsTableAllowedIds,
    MenuGroups
>;

export const initDriverDeclarationsTableHeaders: DriverDeclarationsTableHeader[] =
    [
        {
            title: "Name",
            id: "name",
            menuGroup: MenuGroups.PersonalDetails,
            orderBy: DeclarationsOrderByOptions.LastName,
        },
        {
            title: capitalisedTermForCompany,
            id: "company",
            menuGroup: MenuGroups.Employment,
            orderBy: DeclarationsOrderByOptions.Company,
        },
        {
            title: "Department",
            id: "department",
            menuGroup: MenuGroups.Employment,
            orderBy: DeclarationsOrderByOptions.Department,
        },
        {
            title: "Email",
            id: "email",
            menuGroup: MenuGroups.PersonalDetails,
            orderBy: DeclarationsOrderByOptions.EmailAddress,
        },
        {
            title: "Declaration last sent",
            id: "declarationLastSent",
            menuGroup: MenuGroups.Declaration,
        },
        {
            title: "Declaration expires",
            id: "declarationExpires",
            menuGroup: MenuGroups.Declaration,
        },
        {
            title: "Licence origin",
            id: "licenceOrigin",
            menuGroup: MenuGroups.Declaration,
        },
        {
            title: "Type",
            id: "type",
            menuGroup: MenuGroups.Declaration,
        },
        {
            title: "Status",
            id: "status",
            menuGroup: MenuGroups.Declaration,
        },
        {
            title: "In-Flight Status",
            id: "inFlightStatus",
            menuGroup: MenuGroups.Declaration,
            hide: true,
        },
        {
            title: "In-Flight Type",
            id: "inFlightType",
            menuGroup: MenuGroups.Declaration,
            hide: true,
        },
    ];

export function driverDeclarationsTableContents(
    declarations?: DriverDeclaration[]
): DriverDeclarationsRowContent[] | undefined {
    const tableContents = new Array<DriverDeclarationsRowContent>(0);
    if (!declarations) return undefined;

    declarations.forEach((declaration) => {
        const expirationDate = declaration.declarationExpires
            ? formatDateToString(new Date(declaration.declarationExpires))
            : null;

        const rowContent = (): DriverDeclarationsRowContent => ({
            rowId: declaration.declarationId,
            rowData: declaration,
            cellData: [
                {
                    id: "name",
                    content: (
                        <div className="flex items-center gap-2">
                            <UserIcon
                                firstName={declaration.firstName}
                                lastName={declaration.lastName}
                                size="small"
                            />
                            <p>{`${declaration.firstName}  ${declaration.lastName}`}</p>
                        </div>
                    ),
                },
                {
                    id: "company",
                    content: declaration.companyName,
                },
                {
                    id: "department",
                    content: declaration.department ?? "-",
                },
                {
                    id: "email",
                    content: declaration.emailAddress,
                },
                {
                    id: "declarationExpires",
                    content: expirationDate ?? "-",
                },
                {
                    id: "declarationLastSent",
                    content: declaration.declarationLastSent
                        ? formatDateToString(
                              new Date(declaration.declarationLastSent)
                          )
                        : "-",
                },
                {
                    id: "licenceOrigin",
                    content: declaration.licenceOrigin,
                },
                {
                    id: "type",
                    content: declaration.declarationType,
                },
                {
                    id: "status",
                    content: (
                        <div className="pl-2">
                            <StatusPill
                                status={declaration.declarationStatus ?? "-"}
                                bgColour={declarationStatusColor(
                                    declaration.declarationStatus
                                )}
                            />
                        </div>
                    ),
                },
                {
                    id: "inFlightStatus",
                    content: declaration.inFlightDeclarationStatus ? (
                        <div className="pl-2">
                            <StatusPill
                                status={declaration.inFlightDeclarationStatus}
                                bgColour={declarationStatusColor(
                                    declaration.inFlightDeclarationStatus
                                )}
                            />
                        </div>
                    ) : (
                        ""
                    ),
                },
                {
                    id: "inFlightType",
                    content: declaration.inFlightDeclarationType ?? "",
                },
            ],
        });

        tableContents.push(rowContent());
    });
    return tableContents;
}
